//@flow
import * as Immutable from 'immutable';

import { PjPlanConfiguration } from 'common/models/flow/PjPlanConfiguration';
import type { PjPlanConfigurationModelType } from 'common/models/flow/PjPlanConfiguration';

import { SchedulingConfigurationId } from './SchedulingConfigurationId';
import { PJDistributionPlanConfiguration } from './PJDistributionPlanConfiguration';
import DataUtils from 'common/utils/DataUtils';
import NumberUtils from 'common/utils/NumberUtils';
import I18nHelper from 'common/helpers/I18nHelper';
import {
    validate,
    validatable,
    softValidateAll,
    validateAll
} from 'common/utils/ValidationUtil';
import { getAssignableSalesCategories } from 'common/redux/selectors/salesCategories';
import { DummySalesmanProperties } from 'common/models/internal/DummySalesmanProperties';
import { SalesCategoryGroup } from './SalesCategoryGroup';
import _ from 'lodash';

export const TransportModeEnum = {
    Walking: 'WALKING',
    Vehicle: 'VEHICLE'
};

const translationsNamespace = ['validations', 'entityManagement'];

const nsTranslate = I18nHelper.getTranslate(translationsNamespace);
@validatable
class InternalPjPlanConfiguration extends PjPlanConfiguration {
    static fromJS(
        json: PjPlanConfigurationModelType
    ): InternalPjPlanConfiguration {
        const state: Object = Object.assign({}, json);
        state.schedulingConfigId = SchedulingConfigurationId.fromJS(
            state.schedulingConfigId
        );
        state.distributionPlanConfiguration = PJDistributionPlanConfiguration.fromJS(
            state.distributionPlanConfiguration
        );
        state.salesmanCategoryGroups = state.salesmanCategoryGroups
            ? Immutable.Map(state.salesmanCategoryGroups).map(item =>
                  SalesCategoryGroup.fromJS(item)
              )
            : state.salesmanCategoryGroups;
        state.salesmanCategoryGroupCapacities = state.salesmanCategoryGroupCapacities
            ? Immutable.Map(state.salesmanCategoryGroupCapacities)
            : state.salesmanCategoryGroupCapacities;
        state.dummySalesmanProperties = state.dummySalesmanProperties
            ? Immutable.Map(state.dummySalesmanProperties).map(item =>
                  DummySalesmanProperties.fromJS(item)
              )
            : state.dummySalesmanProperties;
        return new this(Immutable.Map(state));
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        const { distributionPlanConfiguration } = this;
        const capacitiesList = this.getCapacitiesList();
        if (
            distributionPlanConfiguration &&
            distributionPlanConfiguration.hasValidationErrors({
                salesCategories: capacitiesList
            })
        ) {
            return true;
        }
        return false;
    }

    @validateAll
    setAllValidationErrors(): this {
        let { distributionPlanConfiguration } = this;

        if (distributionPlanConfiguration) {
            distributionPlanConfiguration = distributionPlanConfiguration.setAllValidationErrors();
        }
        return this.setDistributionPlanConfiguration(
            distributionPlanConfiguration
        );
    }

    setCapacitiesList(capacitiesList: Immutable.List<Object>): this {
        const updatedCapacities = Immutable.Map(
            capacitiesList
                .map(
                    capacity =>
                        capacity.checked &&
                        !DataUtils.isEmpty(capacity.value) && [
                            capacity.id,
                            capacity.value
                        ]
                )
                .filter(Boolean)
        );
        return this.validateAndSetCapacitiesList(capacitiesList).setCapacities(
            updatedCapacities
        );
    }

    /*
        this is different from usual validations, modifies the list itself
        validations:
            1. integer
            2. non zero
            3. non negative
    */
    validateAndSetCapacitiesList(capacitiesList: Immutable.List<Object>) {
        const newCapacitiesList = capacitiesList.map(capacity => {
            let error;
            if (
                !capacity.checked ||
                (DataUtils.isEmpty(capacity.value) && !capacity.checked)
            ) {
                // if unchecked, or no value + unchecked, clear the errors
                return {
                    ...capacity,
                    error
                };
            }
            if (
                DataUtils.isEmpty(capacity.value) ||
                NumberUtils.toNumber(capacity.value) === 0 ||
                !NumberUtils.isInteger(capacity.value) ||
                NumberUtils.toNumber(capacity.value) < 0
            ) {
                error = nsTranslate('pjpValidationErrors.capacityError');
            }
            return {
                ...capacity,
                error
            };
        });
        return this.set('capacitiesList', newCapacitiesList);
    }

    getCapacitiesList() {
        return this.get('capacitiesList') || Immutable.List();
    }

    getUpdatedCapacitiesList({
        fieldId,
        value,
        checked
    }): Immutable.List<Object> {
        const updatedCapacitiesList = this.getCapacitiesList();
        const existingCapacityIndex = updatedCapacitiesList.findIndex(
            capacity => capacity.id === fieldId
        );
        if (existingCapacityIndex > -1) {
            const existingCapacity = updatedCapacitiesList.get(
                existingCapacityIndex
            );
            return updatedCapacitiesList.set(existingCapacityIndex, {
                ...existingCapacity,
                value,
                checked
            });
        }
        return updatedCapacitiesList.push({ value, id: fieldId, checked });
    }

    get schedulingConfigId(): SchedulingConfigurationId {
        return this._state.get('schedulingConfigId');
    }

    get distributionPlanConfiguration(): PJDistributionPlanConfiguration {
        return this._state.get('distributionPlanConfiguration');
    }

    getDefaultSalesCategoryOptions(): Immutable.List<Object> {
        const salesCategories = this.getCapacitiesList();
        return salesCategories
            .map(({ id, label }) => ({
                value: id,
                label
            }))
            .toJS();
    }

    getSalesCategoryOptions(): Immutable.List<Object> {
        const salesCategories = this.getCapacitiesList();
        const assignedSalesCategories = this.salesmanCategoryGroups;

        const assignableSalesCategories = assignedSalesCategories?.size
            ? getAssignableSalesCategories({
                  salesCategories,
                  assignedSalesCategories
              })
            : salesCategories;

        return assignableSalesCategories
            .map(({ id, label }) => ({
                value: id,
                label
            }))
            .toJS();
    }

    @validate
    setSalesmanCategoryGroups(
        salesmanCategoryGroups: ?Immutable.Map<string, Array<string>>
    ): this {
        return super.setSalesmanCategoryGroups(salesmanCategoryGroups);
    }

    isSalesCategoryListUniqueAcrossGroups(): boolean {
        if (
            !this.salesmanCategoryGroups ||
            !this.salesmanCategoryGroups?.size === 0
        ) {
            return true;
        }
        const chosenSalesCategoriesLists = Object.values(
            this.salesmanCategoryGroups.toJS()
        ).map(group => group.categories.sort());

        return (
            _.uniqWith(chosenSalesCategoriesLists, _.isEqual).length ===
            chosenSalesCategoriesLists.length
        );
    }

    validateSalesmanCategoryGroups(): string {
        if (this.createDummies) {
            if (
                !this.salesmanCategoryGroups?.size ||
                this.salesmanCategoryGroups.some(
                    ({ categories }) => !categories?.size
                )
            ) {
                return nsTranslate(
                    'pjpValidationErrors.emptySalesCategoriesError'
                );
            }
        }
    }

    deleteCreateDummies(): this {
        return this.clone(this._state.delete('createDummies'));
    }

    deleteDummySalesmanProperties(): this {
        return this.clone(this._state.delete('dummySalesmanProperties'));
    }

    deleteSalesmanCategoryGroupCapacities(): this {
        return this.clone(
            this._state.delete('salesmanCategoryGroupCapacities')
        );
    }

    deleteSalesmanCategoryGroups(): this {
        return this.clone(this._state.delete('salesmanCategoryGroups'));
    }

    sanitizeDummyPersona(): this {
        const { createDummies } = this;
        if (!createDummies) {
            return this.deleteCreateDummies()
                .deleteDummySalesmanProperties()
                .deleteSalesmanCategoryGroupCapacities()
                .deleteSalesmanCategoryGroups();
        }
        return this;
    }
}

export { InternalPjPlanConfiguration as PjPlanConfiguration };
